import type { RouteLocationRaw } from "vue-router";
import type { ReminderDto } from "~/api";

export interface ReminderAction {
  label: string;
  route: RouteLocationRaw;
}

// All reminder actions route to the dashboard: ReminderDrawerHost (mounted by
// DashboardV2) watches these query params and opens the matching drawer in place,
// so acting on a notification never navigates away from the dashboard.
export const DASHBOARD_PATH = "/portal/contact-management/dashboard";

/**
 * Maps a notification/reminder to a deep-link action button.
 *
 * The IDs needed to open the drawer already travel with each reminder inside `contextJson`
 * (see the scheduler reminder payloads). We discriminate on the `contextType` key for
 * housing reminders, falling back to the `category` field for contact-activity reminders.
 *
 * Returns `null` (so no button renders) when the reminder type is unknown, the contextJson
 * is missing/unparseable, or the required IDs for that action are absent — this also covers
 * older reminders created before deep-linking was added.
 */
export function getReminderAction(
  reminder: ReminderDto,
): ReminderAction | null {
  let context: Record<string, unknown> = {};
  if (reminder.contextJson) {
    try {
      context = JSON.parse(reminder.contextJson) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  const str = (key: string): string | undefined => {
    const v = context[key];
    return typeof v === "string" && v.length > 0 ? v : undefined;
  };

  // Contact-activity reminders carry no contextType — discriminate on category.
  if (reminder.category === "ActivityDate") {
    const workflowGuid = str("workflowGuid");
    if (!workflowGuid) return null;
    const formResponseGuid = str("formResponseGuid");
    return {
      label: "Open activity",
      route: {
        path: DASHBOARD_PATH,
        query: {
          workflowGuid,
          ...(formResponseGuid ? { formResponseGuid } : {}),
        },
      },
    };
  }

  const contextType = str("contextType");
  if (!contextType) return null;

  const applicationGuid = str("applicationGuid");
  const scenarioGuid = str("scenarioGuid");

  // Lock / Offer → open the scenario review drawer, then the applicant's application.
  if (
    contextType.startsWith("HousingIntakeLock") ||
    contextType.startsWith("HousingOffer")
  ) {
    if (!scenarioGuid || !applicationGuid) return null;
    return {
      label: contextType.startsWith("HousingOffer")
        ? "Review offer"
        : "Continue intake",
      route: {
        path: DASHBOARD_PATH,
        query: { scenarioGuid, applicationGuid },
      },
    };
  }

  // Move-in → open the applicant's Housing Application drawer (Mark Housed lives there).
  if (contextType.startsWith("HousingMoveIn")) {
    if (!applicationGuid) return null;
    return {
      label: "Confirm move-in",
      route: {
        path: DASHBOARD_PATH,
        query: { applicationGuid },
      },
    };
  }

  return null;
}
